import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { getBusinessClientObject } from 'store/authentication/selectors';
import { useEmailFromDomainEnabledEntitlement } from 'hooks/useEmailFromDomainEnabledEntitlement';
import { LabelWithTooltip } from 'components/ToolTipHelpers/LabelWithToolTip';

import Cols from '__components/Layout/Cols';
import { SelectNoScrollCapture as Select } from '__components/Inputs/Select';
import { EmailPicker } from '__components/Kizen/EmailPicker';
import { MergeFieldModalProvider } from 'components/WYSIWYG/components/MergeFieldModal';
import { useDuplicateEmail } from '__pages/AutomationEngine/dialogs/ActionWizard/subsections/hooks/useDuplicateEmail';
import { useAutomationsSelector } from 'pages/AutomationEngine/store/react';
import {
  getMergeArgs,
  getAutomation,
  getAutomationIntializationVariables,
} from 'pages/AutomationEngine/store/selectors';

import { notifyMemberViaEmailTypes } from 'pages/AutomationEngine/helpers';
import { useChangeAutomationsMessages } from '../hooks/useChangeAutomationsMessages';
import Switch from '__components/Kizen/Switch';
import SwitchControl from './SwitchControl';
import { getIsForCustomObject } from 'pages/AutomationEngine/store/selectors';
import { SENDER_TYPE } from 'components/MessageBuilder/components';
import { cleanEmailData } from 'pages/AutomationEngine/dialogs/ActionWizard/utilities';
import { EmailSendOptionsTeamMember } from '../CommonComponents/email-send-options/types';
import { validIntegratedInbox } from '../CommonComponents/helpers';

import {
  EmailSendOptions,
  useEmailSendOptions,
} from '../CommonComponents/email-send-options';

import { IntegratedEmailWarning } from '__pages/Common/bulkactions/StepsContent/IntegratedEmailWarning';
import { useObjectToModifyOptions } from './useObjectToModifyOptions';
import { isTeamMemberValid } from '../CommonComponents/isTeamMemberValid';

export const initState = {
  sendToContactField: null,
  email: null,
  teamMember: {
    type: notifyMemberViaEmailTypes.LAST_ACTIVE,
    role: null,
    employee: null,
  },
  ccTeamMember: {
    type: notifyMemberViaEmailTypes.NONE,
    role: null,
    employees: [],
    field: null,
  },
};

interface NotifyMemberViaEmailProps {
  entityId: string;
  emailType: string;
  values: any;
  onChange: (...args: any[]) => void;
  step: any;
}

interface ValidateProps {
  email: any;
  teamMember: EmailSendOptionsTeamMember;
  ccTeamMember: EmailSendOptionsTeamMember;
  sendToContactField: any;
  translated: boolean;
}

const emptyArray: any[] = [];

export const Component = function NotifyMemberViaEmail(
  props: NotifyMemberViaEmailProps
) {
  const { values, onChange } = props;

  const emailFromDomainEnabled = useEmailFromDomainEnabledEntitlement();

  const { t } = useTranslation();
  const { sendFromOwner = false } = values;
  const contextIsCustomObject = useAutomationsSelector(getIsForCustomObject);

  const automation = useAutomationsSelector(getAutomation);

  const mergeArgs = useAutomationsSelector(getMergeArgs);
  const automationVariables =
    useAutomationsSelector(getAutomationIntializationVariables) || emptyArray;

  const { isForCustomObject } = mergeArgs;

  const onChangeEmail = useChangeAutomationsMessages(values, onChange);

  const emailData = cleanEmailData(values?.email);

  const clientObject = useSelector(getBusinessClientObject);
  const mergeFieldObjects = [clientObject.id];

  if (automation.customObject) {
    mergeFieldObjects.push(automation.customObject.id);
  }

  // duplicate email if pasted data
  useDuplicateEmail(
    emailData,
    onChangeEmail,
    props?.values,
    Boolean(props?.step?.pastedData),
    onChange
  );

  const { objectToModifyOptions } = useObjectToModifyOptions();

  return (
    <div>
      <Cols columns={2} gutter="20px">
        <div>
          <LabelWithTooltip
            label={t(`Send Email to Related Contact(s) in Field`)}
            tooltipText={t(
              'This email will be sent individually to all contacts within the selected relationship field.'
            )}
            placement="top"
          />
          <Select
            placeholder={t('Choose Related Contact')}
            menuInline
            margin
            options={objectToModifyOptions}
            value={values.sendToContactField}
            onChange={(val: any) =>
              onChange({ ...values, sendToContactField: val })
            }
            data-qa="mra-choose-object-field"
          />
        </div>
        {contextIsCustomObject && emailFromDomainEnabled ? (
          <div>
            <SwitchControl
              label={t('Always Send Email from Owner')}
              labelInfo={t(
                'All contact(s) within the selected field will receive the chosen email from the owner of the entity that initiated this automation, regardless of email settings.'
              )}
              labelInfoPlacement={'top'}
            >
              <Switch
                checked={sendFromOwner}
                onChange={({ target }) => {
                  onChange({ ...values, sendFromOwner: target?.checked });
                }}
                data-qa="always-send-email-from-owner"
              />
            </SwitchControl>
          </div>
        ) : null}
      </Cols>
      <Cols
        columns={2}
        gutter={`20px`}
        data-qa="cc-additional-team-member-to-notify"
      >
        <EmailSendOptions
          values={values}
          onChange={onChange}
          transxHook={useEmailSendOptions}
          whichTeamMember={'ccTeamMember'}
          label={t('CC Team Members')}
          addPrefixNone
          allowTrackingWarning
        />
      </Cols>
      <MergeFieldModalProvider
        enableAutomationVariableMergeFields
        enableRecordLinkFieldOption
        automationVariables={automationVariables}
        allowedCustomObjects={mergeFieldObjects}
      >
        <EmailPicker
          automation={automation}
          emailType={props.emailType}
          email={{
            ...emailData,
            sender: {
              ...emailData?.sender,
              subType: sendFromOwner && SENDER_TYPE.ENTITY_RECORD_OWNER,
            },
          }}
          onChange={onChangeEmail}
          readonlySenderInfo={false}
          customObject={isForCustomObject && automation.customObject}
          useAllIntgegratedInboxes
          enableAllObjectMergeFields
          expandAiInPlace
        />
      </MergeFieldModalProvider>
      <IntegratedEmailWarning externalAccount={emailData?.externalAccount} />
    </div>
  );
};

export const validate = ({
  email,
  sendToContactField,
  translated,
  ccTeamMember,
}: ValidateProps) =>
  translated &&
  email &&
  sendToContactField &&
  validIntegratedInbox(email) &&
  isTeamMemberValid(ccTeamMember);
